import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { GalleryDto, TenantDto } from '@nusign/picflow-backend-types';
import { combineLatest, filter, map, of, switchMap } from 'rxjs';
import { billingScopes } from '../../../billing-consts';
import { GalleryInfoDialogComponent } from '../../../gallery/dialogs/gallery-info-dialog/gallery-info-dialog.component';
import { calcGalleryPermissions } from '../../../lib/access-level-helpers';
import { sleep } from '../../../lib/sleep';
import { sortSubGalleries } from '../../../lib/sort-subgalleries';
import { PICFLOW_CHECKOUT } from '../../../picflow-checkout';
import { GalleryStatusColorPipe } from '../../../pipes/gallery-status-color.pipe';
import { GalleryStatusIconPipe } from '../../../pipes/gallery-status-icon.pipe';
import { DialogService } from '../../../services/dialog.service';
import { GalleryAccessLevelService } from '../../../services/gallery-access-level.service';
import { GalleryUrlService } from '../../../services/gallery-url.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { SectionService } from '../../../services/section.service';
import { UpgradeBadgeComponent } from '../../../shared-extra/components/pro-tag/upgrade-badge.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { GalleryTeaserUrlPipe } from '../../../shared/pipes/gallery-teaser-url.pipe';

@Component({
  selector: 'app-gallery-tree',
  templateUrl: './gallery-tree.component.html',
  styleUrls: ['./gallery-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonDirective,
    RouterLink,
    TooltipDirective,
    SvgComponent,
    UpgradeBadgeComponent,
    LoadingIndicatorComponent,
    GalleryTeaserUrlPipe,
    GalleryStatusIconPipe,
    GalleryStatusColorPipe,
  ],
})
export class GalleryTreeComponent {
  private galleryService = inject(GalleryService);
  private permissionService = inject(PermissionService);
  private sectionService = inject(SectionService);
  private accessLevelService = inject(GalleryAccessLevelService);
  private dialog = inject(DialogService);
  private swapGalleryInUrlService = inject(GalleryUrlService);
  private checkoutService = inject(PICFLOW_CHECKOUT);
  readonly gallery = input<GalleryDto>(undefined);
  readonly clientMode = input<boolean>(undefined);
  onAddedGallery = output<void>();
  onClose = output<void>();
  parentGallery = toSignal(
    toObservable(this.gallery).pipe(
      switchMap(gallery => {
        if (gallery.parent_gallery_id) return this.galleryService.getGallery(gallery.parent_gallery_id);
        return of(gallery);
      })
    )
  );
  private accessLevel = toSignal(
    toObservable(this.parentGallery).pipe(
      filter(Boolean),
      switchMap(parentGallery => this.accessLevelService.depricatedGetAccessLevel(parentGallery))
    )
  );
  galleryPermissions = computed(() => calcGalleryPermissions(this.gallery(), this.accessLevel()));
  sections = toSignal(
    toObservable(this.parentGallery).pipe(
      filter(Boolean),
      switchMap(parentGallery => this.sectionService.getSections(parentGallery.id))
    )
  );
  subGalleries = toSignal(
    combineLatest([
      toObservable(this.parentGallery).pipe(filter(Boolean)),
      toObservable(this.sections).pipe(filter(Boolean)),
    ]).pipe(
      switchMap(([parentGallery, sections]) => {
        return this.galleryService.getSubGalleries(parentGallery.id).pipe(
          map(subGalleries => {
            const sortedSubGalleries: GalleryDto[] = [];
            sections
              .filter(section => section.object === 'gallery')
              .forEach(section => {
                const sectionGalleries = subGalleries.filter(gallery => gallery.section === section.id);
                sortedSubGalleries.push(...sortSubGalleries(sectionGalleries, section.sorting));
              });
            return sortedSubGalleries;
          })
        );
      })
    )
  );

  links = computed(() => {
    const subGalleries = this.subGalleries();
    const parentGallery = this.parentGallery();
    if (!subGalleries || !parentGallery) return;
    const links = <{ [galleryId: string]: string[] }>{};
    links[parentGallery['id']] = this.swapGalleryInUrlService.getParentPath(parentGallery);
    subGalleries.forEach(
      gallery => (links[gallery.id] = this.swapGalleryInUrlService.getChildPath(parentGallery, gallery))
    );
    return links;
  });

  creatingSubGallery = signal(false);

  tenantId = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    return (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
  });

  async addSubGallery(e: Event) {
    if (!this.permissionService.canDisplayElement(billingScopes.gallery.subGallery)) {
      this.onClose.emit();
      return this.checkoutService.open(billingScopes.gallery.subGallery);
    }

    e.preventDefault();
    e.stopPropagation();
    if (this.creatingSubGallery()) return;
    this.creatingSubGallery.set(true);
    await this.galleryService.createSubGallery(this.parentGallery());
    this.creatingSubGallery.set(false);
    this.onAddedGallery.emit();
    await sleep(250);
    document.querySelector('app-edit-album-section')?.scrollIntoView();
  }

  openGalleryInfo(gallery: GalleryDto, event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.dialog.open(GalleryInfoDialogComponent, { gallery, inGallery: true });
  }
}
